/**
 * wayai pull [hubs/<hub>|bases/<base>|communities/<community>]
 *            [--hub <id|name>|--base <id|name>|--community <id|folder>] [-y]
 *   — fetch config from the platform and write local files
 *
 * ONE verb, routing by workspace subtree: `hubs/` pulls a hub's config-as-code
 * (below), `bases/` pulls a base's (`data/config-as-code/sync.ts`),
 * `communities/` a community's (`communities/sync.ts`). An invocation that
 * names a target in more than one subtree is REFUSED before any network
 * call and before any file write, never merged and never silently narrowed to
 * one side — the routing precedence and the three refusals live in
 * `lib/subtree-routing.ts`.
 *
 * Within the hubs subtree the hub is derived from the workspace, as before:
 *   - cwd is inside a hub folder → use that hub
 *   - exactly one hub in the hubs dir → use it
 *   - multiple hubs → require `--hub <uuid|folder-name>` to disambiguate
 *
 * For first-time pulls in an empty workspace, pass `--hub <uuid>` to fetch and
 * materialize the hub at `wayai-ws/hubs/<hub-slug>/` (or the legacy
 * `workspace/<hub-slug>/` for un-migrated repos — see `layout.ts`).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { expected } from '../lib/expected.js';
import { communityFoldersHint } from '../lib/community-workspace.js';
import { parseHubFolder } from '../lib/parser.js';
import { printDiff } from '../lib/diff-display.js';
import { confirm, resolveHubYamlPath, UUID_RE } from '../lib/utils.js';
import { prepareResourcePayload } from '../lib/resource-files.js';
import { assertHubFolderConvertible } from '../lib/yaml-writer.js';
import { materializeHubFolder } from '../lib/hub-materializer.js';
import { isWorkspaceRefusal } from '../lib/fs-safety.js';
import { printLocalFileChanges, printWarnings } from './push.js';
import {
  autoRenameHubFolder,
  filterPreviewHubs,
  findGitRoot,
  findEnclosingHubFolder,
  resolveHubFolder,
  resolveWorkspaceDir,
  scanWorkspaceHubs,
} from '../lib/workspace.js';
import { warnLayoutOnce, hubsDirLabel } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { assertInScope, seedScopeIfEmpty } from '../lib/worktree-scope.js';
import { requireSubtree } from '../lib/subtree-routing.js';
import type { CiDiffResponse, HubAsCodePayload } from '../lib/types.js';

interface ParsedArgs {
  autoConfirm: boolean;
}

/**
 * Everything that is NOT the target. The target — `--hub`, a positional, the
 * repo default — is resolved by the subtree router, which has to read it anyway
 * to decide which subtree this invocation belongs to.
 */
function parseArgs(args: string[]): ParsedArgs {
  return { autoConfirm: args.includes('--yes') || args.includes('-y') };
}

/** Resolve the target hub for the pull. May return a not-yet-materialized hub_id. */
export function resolveHubTarget(
  workspaceDir: string,
  selector: string | undefined,
): { hubId: string; hubFolder: string | null } {
  // Resolved once: `findGitRoot` spawns `git`, and the label and the community hint both need it.
  const gitRoot = findGitRoot();
  const wsLabel = hubsDirLabel(gitRoot);
  const allHubs = scanWorkspaceHubs(workspaceDir);

  // An empty selector (an unset variable) names nothing: refused below as a miss, never read as none.
  if (selector !== undefined) {
    // Match against existing folders by hubId or basename. Searches ALL folders
    // (incl. production mirrors) so a mirror can be refreshed directly.
    const match = allHubs.find((h) =>
      h.hubId === selector || path.basename(h.hubFolder) === selector,
    );
    if (match) return { hubId: match.hubId, hubFolder: match.hubFolder };

    // Selector might be a UUID for a hub that hasn't been materialized yet.
    if (UUID_RE.test(selector)) return { hubId: selector, hubFolder: null };

    // Names the selector, not the flag: it can now arrive as a positional
    // (`wayai pull hubs/<hub>`) or as the repo default, and pointing the user at
    // `--hub` when they typed neither is a wrong instruction.
    console.error(`No hub matching "${selector}" found in ${wsLabel}/. Pass a UUID for first-time pulls.`);
    process.exit(1);
  }

  const enclosing = findEnclosingHubFolder(workspaceDir);
  if (enclosing) return { hubId: enclosing.hubId, hubFolder: enclosing.hubFolder };

  // Auto-select ignores read-only production mirror folders so a preview + its
  // mirror still resolves to the single preview.
  const previewHubs = filterPreviewHubs(allHubs);

  if (previewHubs.length === 1) return { hubId: previewHubs[0].hubId, hubFolder: previewHubs[0].hubFolder };

  if (previewHubs.length === 0) {
    console.error(`No hub folders found in ${wsLabel}/. Pass --hub <uuid> to fetch a hub for the first time.`);
    const hint = communityFoldersHint(gitRoot, 'pull');
    if (hint) console.error(hint);
    process.exit(1);
  }

  console.error(`Multiple hubs found in ${wsLabel}/. Pass --hub <uuid|folder-name> to choose, or run from inside a hub folder:`);
  for (const h of previewHubs) {
    console.error(`  ${path.basename(h.hubFolder)} (${h.hubId})`);
  }
  process.exit(1);
}

export async function pullCommand(args: string[]): Promise<void> {
  // Subtree routing FIRST — ahead of the repo config, the login, the first
  // request and the workspace-file scaffolding below, so a mixed invocation is
  // refused before it can touch the network or the working tree.
  // Resolved once: `findGitRoot` spawns `git`, and both the router and the
  // command below need the answer.
  const gitRoot = findGitRoot();
  const route = requireSubtree('pull', args, gitRoot);
  if (gitRoot) warnLayoutOnce(gitRoot);
  if (route.subtree === 'bases') {
    const { pullBase } = await import('../data/config-as-code/sync.js');
    await pullBase(gitRoot, route.selector, args);
    return;
  }
  if (route.subtree === 'communities') {
    const { pullCommunity } = await import('../communities/sync.js');
    await pullCommunity(gitRoot, route.selector, args);
    return;
  }

  const { autoConfirm } = parseArgs(args);
  // The router resolved the hub selector from `--hub` or the positional; the
  // within-subtree fallbacks (enclosing folder, sole preview) stay below.
  const hubSelector = route.selector;
  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  // Determine the hubs directory for the active layout.
  const workspaceDir = resolveWorkspaceDir();

  const { hubId, hubFolder: existingFolder } = resolveHubTarget(workspaceDir, hubSelector);

  // Fetch remote hub config
  console.log('Fetching hub configuration...');
  const payload = await client.pull(hubId, organizationId).catch((err: unknown) => {
    // A bare UUID matching no hub folder may be a community's id: those are named explicitly.
    if (!existingFolder && err instanceof ApiError && err.status === 404) {
      throw expected(`No hub ${hubId} in this organization. If it is a community: wayai pull communities/${hubId}`);
    }
    throw err;
  });

  if (payload.hub_environment === 'production') {
    // Directly-targeted production hub → materialize a read-only mirror (bare
    // slug, no preview suffix). Not scoped: the mirror is browse-only, and push
    // is blocked for production at the route + DO. Failure here is the primary
    // action's failure, so let it surface.
    const folder = await writeProductionMirror(workspaceDir, payload);
    console.log(`Production hub mirrored (read-only) → ${folder}`);
    return;
  }

  // Preview hub → enforce the worktree scope before writing editable source.
  assertInScope('hubs', hubId);

  // A never-pulled hub's path may already hold a hub config file the workspace scan could not
  // read (a broken hub.yaml). Such a folder takes the existing-folder path below, which reads it
  // before writing anything (#4586).
  const hubFolder = existingFolder ?? resolveHubFolder(
    workspaceDir,
    hubId,
    payload.hub.name,
    payload.hub_environment,
    payload.preview_label,
    payload.branch_name,
  );
  let wroteFiles = false;

  // If no existing folder, create path for first pull
  if (!existingFolder && !resolveHubYamlPath(hubFolder)) {
    // Ensure parent directories exist
    fs.mkdirSync(path.dirname(hubFolder), { recursive: true });

    console.log('Writing hub configuration...');
    await materializeHubFolder(hubFolder, payload);
    const finalFolder = autoRenameHubFolder(hubFolder, payload.hub.name, payload.hub_environment, payload.hub_id, payload.preview_label, payload.branch_name);
    console.log(`Hub configuration written to ${finalFolder}`);
    wroteFiles = true;
  } else {
    // Hub folder exists — show diff before overwriting. Neither catch below spans the
    // materializer: a materializer failure must propagate, because falling into an
    // overwrite retry would re-run the materializer on the same (already-rewritten)
    // payload and trip its double-run guard.
    let action: 'sync_metadata' | 'apply' | 'overwrite' = 'overwrite';
    const replaceWithoutDiff = (why: string, err: unknown) => confirmReplaceWithoutDiff(why, err, hubFolder, hubId, autoConfirm);
    // A folder an older CLI wrote keeps its skills under `resources/`, which this pull converts:
    // anything else there (a knowledge backup), or already in its `skills/`, stops it now, before
    // the diff and the question (wayai-pro/wayai#5142 step 5 D1b). The write checks it again.
    assertHubFolderConvertible(hubFolder, payload);
    let localPayload: HubAsCodePayload | undefined;
    try {
      // Strict: a file the parser would skip is one this pull would remove uncompared. An older
      // CLI's layout (`resources:`, `resources/`) is read as its skills, to compare before converting.
      localPayload = parseHubFolder(hubFolder, { strict: true, readOldLayout: true });
    } catch (err) {
      // A path the parser refuses (a symlink, an instructions path escaping agents/) stops the
      // pull outright: overwriting would replace the user's local edits unprompted.
      if (isWorkspaceRefusal(err)) throw err;
      // A replacement agreed to without a diff falls through to the overwrite below.
      if (!(await replaceWithoutDiff(UNREADABLE_LOCAL, err))) {
        console.log('Cancelled.');
        return;
      }
    }
    let diffResult: CiDiffResponse | undefined;
    if (localPayload) {
      try {
        diffResult = await client.diff(hubId, prepareResourcePayload(localPayload, payload), 'pull', organizationId);
      } catch (err) {
        // A replacement agreed to without a diff falls through to the overwrite below.
        if (!(await replaceWithoutDiff(UNCOMPARABLE_LOCAL, err))) {
          console.log('Cancelled.');
          return;
        }
      }
    }
    if (localPayload && diffResult) {
      // Rejections and advisories print ahead of the diff and the prompt, as push prints
      // them. A pull's diff keeps every write a rejection refuses or a push skips (#4571,
      // #4586), so such a local edit is listed below and asked about rather than
      // overwritten unseen.
      printWarnings(diffResult.warnings);

      // Connections are read-only references excluded from the diff engine.
      // Compare them separately so new/removed connections trigger a pull write.
      const connectionsChanged = !connectionsEqual(localPayload.connections, payload.connections);

      if (!diffResult.has_changes && !connectionsChanged) {
        // No semantic changes, but still write the remote payload to sync
        // metadata (tool IDs, connection IDs, etc.) that the diff engine ignores.
        action = 'sync_metadata';
      } else {
        console.log('\nChanges to apply locally:');
        if (connectionsChanged) {
          console.log('**Connections:** updated');
        }
        printDiff(diffResult.summary);

        if (!autoConfirm) {
          const ok = await confirm('Apply these changes?');
          if (!ok) {
            console.log('Cancelled.');
            return;
          }
        }
        action = 'apply';
      }
    }

    if (action === 'overwrite') console.log('Writing hub configuration...');
    const delta = await materializeHubFolder(hubFolder, payload);
    const finalFolder = autoRenameHubFolder(hubFolder, payload.hub.name, payload.hub_environment, payload.hub_id, payload.preview_label, payload.branch_name);
    wroteFiles = true;
    if (action === 'sync_metadata') {
      if (finalFolder === hubFolder) {
        // The diff engine saw no semantic change, but metadata the engine ignores (tool IDs,
        // connection IDs, etc.) may still have been written — NAME the files that actually
        // changed rather than a blanket "no config changes" line (gh #3410).
        printLocalFileChanges(delta);
      } else {
        console.log('Metadata synced; folder renamed.');
      }
    } else {
      // apply (user confirmed a diff) / overwrite (user agreed to replace a folder that could
      // not be compared): name the files that changed on disk too, not just the folder — any
      // local write announces itself (gh #3410). The first-pull branch above stays quiet: a
      // brand-new folder is all-new, so a full listing is noise (same reasoning as
      // replicate's fresh case).
      console.log(`Hub configuration written to ${finalFolder}`);
      printLocalFileChanges(delta);
    }
  }

  // Seed the worktree's hubs axis on first successful pull. No-op if that axis
  // already holds anything (even this same hub) or if not in git.
  if (wroteFiles) seedScopeIfEmpty('hubs', hubId);

  // Mirror the linked production hub read-only so the agent always has the live
  // production config alongside the preview. Best-effort — the preview pull has
  // already succeeded, so a mirror failure must NOT fail the command.
  if (payload.production_hub_id) {
    await mirrorLinkedProduction(client, workspaceDir, payload.production_hub_id, organizationId);
  }
}

/** Why the changes cannot be listed: the CLI could not read a local file (#4586). */
const UNREADABLE_LOCAL = 'The CLI could not read your local files, so the changes cannot be listed:';
/** Why the changes cannot be listed: the diff request failed (#4571). */
const UNCOMPARABLE_LOCAL = 'The platform could not compare your local files, so the changes cannot be listed:';

/**
 * A local folder that cannot be compared leaves no changes to list, so pull replaces the local
 * files only when the user says so. The reason prints first. The CLI may be unable to read a
 * local file (#4586), or the diff may fail (#4571): the platform refusing a local edit (a
 * `pilot_voice` agent while voice calls are off), the stored config itself (a reference gone
 * stale on the platform, where replacing is the only refresh), or any other error.
 * Interactively the user decides. Under `-y` there is nothing listed to agree to, so the pull
 * always stops: it writes nothing and rethrows, which exits non-zero, after naming the two ways
 * to decide instead.
 */
async function confirmReplaceWithoutDiff(
  why: string,
  err: unknown,
  hubFolder: string,
  hubId: string,
  autoConfirm: boolean,
): Promise<boolean> {
  console.log(`\n${why}`);
  console.log(`  ${err instanceof Error ? err.message : String(err)}`);
  if (!autoConfirm) {
    return confirm('Replace the local files with the platform configuration, discarding any local edits?');
  }
  console.error(
    'Nothing was written: -y replaces local files only with changes it can list. To decide, run `wayai pull` '
      + `without -y. To re-pull fresh, move ${hubFolder} out of the workspace and run \`wayai pull --hub ${hubId}\`.`,
  );
  throw err;
}

/**
 * Materialize a production hub as a READ-ONLY mirror folder (bare slug, no
 * preview suffix). The CLI cannot push production (blocked at the route + DO),
 * so this is a browse-only snapshot, refreshed on every pull of its preview.
 * Never binds the worktree. Returns the final folder path.
 */
export async function writeProductionMirror(
  workspaceDir: string,
  prodPayload: HubAsCodePayload,
): Promise<string> {
  const folder = resolveHubFolder(workspaceDir, prodPayload.hub_id, prodPayload.hub.name, 'production', null, null);
  fs.mkdirSync(path.dirname(folder), { recursive: true });
  // Read-only mirror: no agent-context authoring scaffold (browse-only, never synced).
  await materializeHubFolder(folder, prodPayload, { seedAgentContext: false, readOnlyMirror: true });
  const finalFolder = autoRenameHubFolder(folder, prodPayload.hub.name, 'production', prodPayload.hub_id, null, null);
  prependMirrorMarker(finalFolder, prodPayload.hub_id);
  return finalFolder;
}

/** Fetch + write a preview's linked production hub as a read-only mirror. Best-effort. */
async function mirrorLinkedProduction(
  client: ApiClient,
  workspaceDir: string,
  productionHubId: string,
  organizationId?: string,
): Promise<void> {
  try {
    const prodPayload = await client.pull(productionHubId, organizationId);
    const folder = await writeProductionMirror(workspaceDir, prodPayload);
    console.log(`Mirrored production hub → ${folder} (read-only)`);
  } catch (err) {
    console.warn(`Warning: could not mirror production hub (${err instanceof Error ? err.message : String(err)}). Preview pull is unaffected.`);
  }
}

/** Marker that an agent/human is editing a read-only mirror, not source. Cosmetic — never throws. */
const MIRROR_MARKER_PREFIX = '# Read-only mirror of production hub';
function prependMirrorMarker(hubFolder: string, productionHubId: string): void {
  const hubYaml = path.join(hubFolder, 'hub.yaml');
  try {
    const content = fs.readFileSync(hubYaml, 'utf-8');
    if (content.startsWith(MIRROR_MARKER_PREFIX)) return;
    const marker = `${MIRROR_MARKER_PREFIX} ${productionHubId}. Edits are ignored; push is blocked. Edit the linked preview hub instead.\n`;
    fs.writeFileSync(hubYaml, marker + content, 'utf-8');
  } catch {
    // Marker is cosmetic; the route/DO guards are the real protection.
  }
}

/**
 * Compare two connection arrays by id set.
 * Connections are read-only references — we only care whether the set changed
 * (added, removed, or renamed), not about field-level diffs.
 */
type ConnectionRef = { id?: string; name: string; type: string; service?: string };
export function connectionsEqual(
  a: ConnectionRef[] | undefined,
  b: ConnectionRef[] | undefined,
): boolean {
  const listA = a || [];
  const listB = b || [];
  if (listA.length !== listB.length) return false;

  // Build a comparable key per connection (id if available, otherwise name+type+service)
  const key = (c: ConnectionRef) =>
    c.id || `${c.name}|${c.type}|${c.service || ''}`;

  const setA = new Set(listA.map(key));
  const setB = new Set(listB.map(key));
  if (setA.size !== setB.size) return false;
  for (const k of setA) {
    if (!setB.has(k)) return false;
  }
  return true;
}
